import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Checkbox, Radio, Select } from '../UI';
import { formatPrice, PriceCurrency } from '../../utils/formatCurrency';

export type PriceDisplayCurrency = 'both' | PriceCurrency;

export interface PriceDisplay {
  enabled: boolean;
  communitySlug: string;
  currency: PriceDisplayCurrency;
}

export const emptyPriceDisplay = (): PriceDisplay => ({ enabled: false, communitySlug: '', currency: 'both' });

interface CommunityOption {
  slug: string;
  name: string;
  enabled: boolean;
  interval: 'month' | 'year';
  prices?: Partial<Record<PriceCurrency, { amount?: number; stripePriceId?: string }>>;
}

interface Props {
  value: PriceDisplay;
  onChange: (next: PriceDisplay) => void;
  /** Comunidade sugerida ao ligar (a do CTA ou a do quiz). */
  suggestedSlug: string;
}

const CURRENCY_LABELS: Record<PriceDisplayCurrency, string> = { both: 'Euro e real', EUR: 'Só euro', BRL: 'Só real' };
const CURRENCIES: PriceCurrency[] = ['EUR', 'BRL'];

/**
 * Valor de uma comunidade paga exibido perto do botão da LP. Só guarda a comunidade e as moedas;
 * a página lê o preço da comunidade a cada acesso (backend/utils/landingPagePrice.js), com a
 * mesma regra da oferta: só aparece a moeda à venda (com Price na Stripe) e a venda aberta.
 */
const PriceDisplayPicker: React.FC<Props> = ({ value, onChange, suggestedSlug }) => {
  const [communities, setCommunities] = useState<CommunityOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!value.enabled) return;
    let cancelled = false;
    setLoading(true);
    axios.get('/community/admin')
      .then((res) => { if (!cancelled) setCommunities(res.data.communities || []); })
      .catch(() => { if (!cancelled) setCommunities([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [value.enabled]);

  const selected = communities.find((c) => c.slug === value.communitySlug);
  const wanted = value.currency === 'both' ? CURRENCIES : [value.currency];
  const onSale = selected
    ? wanted.filter((c) => selected.prices?.[c]?.stripePriceId && (selected.prices?.[c]?.amount || 0) > 0)
    : [];
  const preview = selected && selected.enabled && onSale.length
    ? `${onSale.map((c) => formatPrice(selected.prices![c]!.amount as number, c, 'pt')).join(' · ')} ${selected.interval === 'year' ? '/ano' : '/mês'}`
    : '';
  const missing = selected ? wanted.filter((c) => !onSale.includes(c)) : [];

  return (
    <div className="space-y-3">
      <Checkbox
        checked={value.enabled}
        onChange={(e) => onChange({
          ...value,
          enabled: e.target.checked,
          communitySlug: e.target.checked && !value.communitySlug ? suggestedSlug : value.communitySlug
        })}
        label="Mostrar o valor de uma comunidade na página"
        description="Aparece acima do botão, no topo e no fim da página. O valor vem da comunidade: mudou lá, muda aqui."
      />

      {value.enabled && (
        <>
          <Select
            value={value.communitySlug}
            onChange={(e) => onChange({ ...value, communitySlug: e.target.value })}
            disabled={loading}
            label="Comunidade"
            className="field--lg"
          >
            <option value="">{loading ? 'Carregando…' : 'Selecione'}</option>
            {value.communitySlug && !loading && !selected && (
              <option value={value.communitySlug}>{value.communitySlug} (não encontrada na lista)</option>
            )}
            {communities.map((c) => (
              <option key={c.slug} value={c.slug}>{c.name}{c.enabled ? '' : ' (venda fechada)'}</option>
            ))}
          </Select>

          <div role="radiogroup" aria-label="Moeda exibida" className="flex flex-wrap gap-x-6">
            {(Object.keys(CURRENCY_LABELS) as PriceDisplayCurrency[]).map((c) => (
              <Radio
                key={c}
                name="priceDisplayCurrency"
                checked={value.currency === c}
                onChange={() => onChange({ ...value, currency: c })}
                label={CURRENCY_LABELS[c]}
              />
            ))}
          </div>

          {preview && (
            <p className="text-sm text-gray-700">Na página: <strong>{preview}</strong></p>
          )}
          {selected && !selected.enabled && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
              A venda desta comunidade está fechada: o valor não aparece na página até ela ser reaberta.
            </p>
          )}
          {selected && selected.enabled && missing.length > 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
              {onSale.length
                ? `A comunidade não vende em ${missing.map((c) => (c === 'EUR' ? 'euro' : 'real')).join(' e ')}: só o valor à venda aparece.`
                : 'A comunidade não vende nesta moeda: o valor não aparece na página.'}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default PriceDisplayPicker;
